import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Feature } from 'app/core/map/layers/layer.model';
import * as L from 'leaflet';
import { isMultiPoint, isPoint, MultiPoint, multiPointToPoints, Point, Position } from '../models/geometry';
import { LMapService } from '../services';


type Marker = Feature<Point> & { properties: Record<string, unknown> };

const isMarker = (f: Feature): f is Marker => isPoint(f.geometry) || isMultiPoint(f.geometry);


export class Icon extends L.Icon {

    constructor(options?: L.IconOptions & { iconId?: string }) {
        super({
            iconUrl: '/assets/images/markers.svg#defaultMarker',
            iconId: 'defaultMarker',
            iconSize: [28, 50],
            iconAnchor: [14, 50],
            popupAnchor: [14, 50],
            ...options,
        });
    }

    createIcon() {
        const svgNs = 'http://www.w3.org/2000/svg';
        const xlinkNs = 'http://www.w3.org/1999/xlink';
        const svg = document.createElementNS(svgNs, 'svg');
        // @ts-ignore
        svg.setAttribute('width', `${this.options.iconSize?.[0]}`);
        // @ts-ignore
        svg.setAttribute('height', `${this.options.iconSize?.[1]}`);
        const use = document.createElementNS(svgNs, 'use');
        // @ts-ignore
        use.setAttribute('class', this.options['iconId']);
        use.setAttributeNS(xlinkNs, 'href', this.options.iconUrl);
        svg.appendChild(use);
        // @ts-ignore
        svg.style.marginLeft = `${-this.options.iconAnchor?.[0]}px`;
        // @ts-ignore
        svg.style.marginTop = `${-this.options.iconAnchor?.[1]}px`;
        return svg as any;  // SVGSVGElement is not compatible with HTMLElement
    }
}

const defaultIcon = new Icon();

L.Marker.prototype.options.icon = defaultIcon;


type IconFunction = (index: number, feature: Feature) => L.Icon;

const defaultIconFn: IconFunction = (i, f) => {
    if (f.style?.iconUrl) {
        return new Icon(f.style as L.IconOptions);
    }
    return defaultIcon;
};


type TrackByFunction = (index: number, feature: Feature) => string;

const defaultTrackByFn: TrackByFunction = (i, f) => {
    if (!f.id) {
        return i.toString();
    }
    return f.id.toString();
};


@Component({
    selector: 'l-markers-group',
    template: ``,
})
export class LMarkersGroupComponent<T extends Feature<Point | MultiPoint>> implements OnInit, OnChanges, OnDestroy {

    @Input() markers: T[] = [];

    @Input() iconFn: IconFunction = defaultIconFn;

    @Input() trackBy: TrackByFunction = defaultTrackByFn;

    @Input() disableClusteringAtZoom = 18;

    @Output() markerClick = new EventEmitter<{ position: Position, feature: T, markerId: string }>();

    @Output() markerPositionChange = new EventEmitter<{ position: Position, markerId: string}>();

    @Output() markerMouseover = new EventEmitter<{ position: Position, feature: T }>();

    @Output() markerMouseout = new EventEmitter<{ position: Position, feature: T }>();

    private _markers: { [id: string]: L.Marker } = {};

    private group: any;

    constructor(protected mapService: LMapService) {}

    ngOnInit() {
        // this.group = L.markerClusterGroup({
        //     spiderfyOnMaxZoom: false,
        //     disableClusteringAtZoom: this.disableClusteringAtZoom,
        //     showCoverageOnHover: false,
        //     animate: false,
        // });
        this.group = new L.FeatureGroup();

        this.mapService.addGroup('markers', this.group);

        this.group.on('click', (event: L.LeafletMouseEvent) => {
            this.markerClick.emit({
                position: [event.latlng.lng, event.latlng.lat],
                feature: event.propagatedFrom.feature,
                markerId: (event.propagatedFrom as L.Marker & { markerId: string }).markerId,
            });
        });

        this.group.on('mouseover', (event: L.LeafletMouseEvent) => {
            this.markerMouseover.next({
                position: [event.latlng.lng, event.latlng.lat],
                feature: event.propagatedFrom.feature,
            });
        });

        this.group.on('mouseout', (event: L.LeafletMouseEvent) => {
            this.markerMouseout.next({
                position: [event.latlng.lng, event.latlng.lat],
                feature: event.propagatedFrom.feature,
            });
        });

        this.updateMarkers(this.markers);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['markers'] && !changes['markers'].firstChange) {
            this.updateMarkers(changes['markers'].currentValue);
        }
    }

    private updateMarkers(features: Feature[]) {
        const markersToAdd: [string, Marker][] = [];

        const pointFeatures = this.filterAndConvertPointFeatures(features) as Marker[];
        pointFeatures.forEach((f, index) => {
            const markerId = this.trackBy(index, f);
            if (!this._markers[markerId]) {
                markersToAdd.push([markerId, f as Marker]);
            } else {
                const marker = this._markers[markerId];

                if (!marker.getLatLng().equals(L.latLng(
                    f.geometry.coordinates[1],
                    f.geometry.coordinates[0],
                ))) {
                    marker.setLatLng([
                        f.geometry.coordinates[1],
                        f.geometry.coordinates[0],
                    ]);

                    this.markerPositionChange.emit({
                        markerId,
                        position: (f as Marker).geometry.coordinates,
                    });
                }

                marker.feature = f as Marker;
                marker.setIcon(this.iconFn(index, f));
            }
        });

        const markerIdsToRemove: string[] = [];
        for (const id of Object.keys(this._markers)) {
            if (!pointFeatures.find((f, i) => this.trackBy(i, f) === id)) {
                markerIdsToRemove.push(id);
            }
        }

        this.addMarkers(markersToAdd);
        this.removeMarkers(markerIdsToRemove);
    }

    private filterAndConvertPointFeatures(features: Feature[]) {
        const pointFeatures: Feature[] = [];
        for (const f of features) {
            if (!isMarker(f)) {
                continue;
            }

            if (isMultiPoint(f.geometry)) {
                const pointsFeaturesFromMultipoint = this.convertMultiPointToPoint(f);
                pointFeatures.push(...pointsFeaturesFromMultipoint);
            } else {
                pointFeatures.push(f);
            }
        }

        return pointFeatures;
    }

    private convertMultiPointToPoint(feature: Feature) {
        return multiPointToPoints(feature.geometry as MultiPoint)
            .map((pointGeometry, index) => {
                return {
                    ...feature,
                    id: feature.id + index,     // needed for this.trackBy otherwise tracked only one point
                    geometry: pointGeometry,
                };
            });
    }

    private addMarkers(features: [string, Marker][]) {
        const markers = features.map(([id, f], index) => {
            const options: L.MarkerOptions = {
                icon: this.iconFn(index, f),
            };
            const marker = L.marker(L.latLng(f.geometry.coordinates[1], f.geometry.coordinates[0]), options);
            marker.feature = f;
            (marker as L.Marker & { markerId: string }).markerId = id;
            this._markers[id] = marker;
            return marker;
        });
        // this.group.addLayers(markers);
        markers.forEach(m => this.group.addLayer(m));
    }

    private removeMarkers(ids: string[]) {
        const markersToRemove: L.Marker[] = [];
        for (const id of ids) {
            markersToRemove.push(this._markers[id]);
            delete this._markers[id];
        }
        // this.group.removeLayers(markersToRemove);
        markersToRemove.forEach(m => this.group.removeLayer(m));
    }

    ngOnDestroy() {
        this.group.clearAllEventListeners();
        this.group.clearLayers();
        this.group.remove();
    }
}
